<!-- Fishing is scoped by zone and hunting by clan area (listed below), so the generic
     location row is normally hidden. But the staff edit form can set locations on these
     types too - show the row when it actually carries a value, so a staff-set location is
     never editable-but-invisible. -->
<app-permit-common-details
    [permit]="permit"
    [showLocation]="!!(permit.locations?.length || permit.locationOther || permit.location)"
></app-permit-common-details>

@if (permit.fishingHunting) {
    <dl class="row mb-0 mt-2">
        <dt class="col-sm-4">Term</dt>
        <dd class="col-sm-8">{{ permit.fishingHunting.term === 'annual' ? 'Annual' : '14-day' }}</dd>

        @if (permit.fishingHunting.referencedPermitNumber) {
            <dt class="col-sm-4">{{ permit.fishingHunting.referencedPermitLabel || 'Referenced permit #' }}</dt>
            <dd class="col-sm-8">{{ permit.fishingHunting.referencedPermitNumber }}</dd>
        }

        <dt class="col-sm-4">{{ permit.fishingHunting.documentLabel || 'Document' }}</dt>
        <dd class="col-sm-8">
            @if (documentUrl(permit.fishingHunting.document)) {
                @if (isImage(permit.fishingHunting.document)) {
                    <!-- The reviewer is approving this permit by looking at the photo, so show it
             rather than making them open a tab. Still a link, for a proper look. -->
                    <a [href]="documentUrl(permit.fishingHunting.document)" target="_blank" rel="noopener" class="d-inline-block">
                        <img
                            [src]="documentUrl(permit.fishingHunting.document)"
                            [alt]="permit.fishingHunting.document?.filename || 'Uploaded photo'"
                            class="uploaded-photo rounded border"
                            loading="lazy"
                        />
                    </a>
                    <div class="small text-muted mt-1">{{ permit.fishingHunting.document?.filename }}</div>
                } @else {
                    <a [href]="documentUrl(permit.fishingHunting.document)" target="_blank" rel="noopener">
                        <i class="fa fa-file-pdf me-1"></i>{{ permit.fishingHunting.document?.filename || 'View document' }}
                    </a>
                }
            } @else {
                -
            }
        </dd>

        <!-- Annual hunting only (TLC, 30 Sep). Null on fishing and on 14-day permits,
             so the row shows only when a vehicle photo was collected. -->
        @if (permit.fishingHunting.vehiclePhoto) {
            <dt class="col-sm-4">Vehicle photo</dt>
            <dd class="col-sm-8">
                @if (isImage(permit.fishingHunting.vehiclePhoto)) {
                    <a [href]="documentUrl(permit.fishingHunting.vehiclePhoto)" target="_blank" rel="noopener" class="d-inline-block">
                        <img
                            [src]="documentUrl(permit.fishingHunting.vehiclePhoto)"
                            [alt]="permit.fishingHunting.vehiclePhoto.filename || 'Vehicle photo'"
                            class="uploaded-photo rounded border"
                            loading="lazy"
                        />
                    </a>
                    <div class="small text-muted mt-1">{{ permit.fishingHunting.vehiclePhoto.filename }}</div>
                } @else {
                    <a [href]="documentUrl(permit.fishingHunting.vehiclePhoto)" target="_blank" rel="noopener">
                        <i class="fa fa-file me-1"></i>{{ permit.fishingHunting.vehiclePhoto.filename || 'View photo' }}
                    </a>
                }
            </dd>
        }
    </dl>

    <div class="d-flex align-items-start justify-content-between flex-wrap mt-3 mb-1">
        <div>
            <h2 class="h6 mt-0 mb-1">{{ areaWordPlural }}</h2>
            @if (canDecide) {
                <p class="text-muted small mb-0">
                    Each area is consulted with its own clan, so they can be decided separately. Decisions can be changed until the permit itself is approved or
                    denied.
                </p>
            }
        </div>
        @if (canDecide && !deciding) {
            <div class="text-nowrap">
                @if (withheldCount) {
                    <button type="button" class="btn btn-sm btn-outline-secondary me-1" (click)="releaseAll()" [disabled]="saving">
                        <i class="fa fa-eye me-1"></i>Show all results to applicant ({{ withheldCount }})
                    </button>
                }
                @if (pendingCount) {
                    <button type="button" class="btn btn-sm btn-success clan-approve-all" (click)="startApproveRemaining()" [disabled]="saving">
                        <i class="fa fa-check me-1"></i>Approve all remaining ({{ pendingCount }})
                    </button>
                }
            </div>
        }
    </div>

    @if (permit.fishingHunting.clanAreaSelections?.length) {
        <div class="table-responsive">
            <table class="table align-middle mb-0 zones-table">
                <thead>
                    <tr>
                        <th>Area</th>
                        <th>Decision</th>
                        <th class="text-end">Fee</th>
                        @if (canDecide) {
                            <th>Decided by</th>
                            <th class="text-end">Actions</th>
                        }
                    </tr>
                </thead>
                <tbody>
                    @for (area of permit.fishingHunting.clanAreaSelections; track area.clanArea) {
                        <tr>
                            <td>{{ area.clanArea }}</td>
                            <td>
                                <span class="badge" [ngClass]="badgeClass(area.status)">{{ area.statusLabel }}</span>
                                @if (canDecide) {
                                    @if (area.staffNote) {
                                        <div class="small text-muted">{{ area.staffNote }}</div>
                                    }
                                    @if (area.applicantNote) {
                                        <div class="small fst-italic">To applicant: {{ area.applicantNote }}</div>
                                    }
                                    @if (area.visibleToApplicant === false && area.status !== 'pending') {
                                        <div class="small text-warning-emphasis"><i class="fa fa-eye-slash me-1"></i>Applicant still sees this as Pending</div>
                                    }
                                } @else if (area.note) {
                                    <div class="small text-muted">{{ area.note }}</div>
                                }
                            </td>
                            <td class="text-end">{{ area.price | currency: 'AUD' : '$' }}</td>
                            @if (canDecide) {
                                <td class="small text-muted">
                                    @if (area.decidedAt) {
                                        {{ area.decidedBy || 'System' }}<br />{{ area.decidedAt }}
                                    } @else {
                                        -
                                    }
                                </td>
                                <td class="text-end text-nowrap">
                                    @if (area.status === 'pending') {
                                        <button type="button" class="btn btn-sm btn-outline-success" (click)="start(area, 'approved')" [disabled]="saving">
                                            Approve
                                        </button>
                                        <button type="button" class="btn btn-sm btn-outline-danger ms-1" (click)="start(area, 'rejected')" [disabled]="saving">
                                            Reject
                                        </button>
                                    } @else {
                                        <!-- Already decided: offer the opposite decision (spelled out, so it
                         doesn't read as "approve this again"), plus a way back into the notes. -->
                                        @if (area.status === 'approved') {
                                            <button type="button" class="btn btn-sm btn-outline-danger" (click)="start(area, 'rejected')" [disabled]="saving">
                                                Change to rejected
                                            </button>
                                        } @else {
                                            <button type="button" class="btn btn-sm btn-outline-success" (click)="start(area, 'approved')" [disabled]="saving">
                                                Change to approved
                                            </button>
                                        }
                                        <button type="button" class="btn btn-sm btn-link" (click)="editNote(area)" [disabled]="saving">Edit note</button>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-link text-decoration-none"
                                            (click)="toggleVisibility(area)"
                                            [disabled]="saving"
                                            [title]="
                                                area.visibleToApplicant === false
                                                    ? 'Show this result to the applicant'
                                                    : 'Hide this result - the applicant will see Pending'
                                            "
                                        >
                                            <i
                                                class="fa"
                                                [class.fa-eye]="area.visibleToApplicant === false"
                                                [class.fa-eye-slash]="area.visibleToApplicant !== false"
                                            ></i>
                                        </button>
                                    }
                                </td>
                            }
                        </tr>
                    }
                </tbody>
            </table>
        </div>

        @if (canDecide && !permit.fishingHunting.allClanAreasDecided && !deciding) {
            <p class="small text-muted mt-2 mb-0">
                <i class="fa fa-circle-info me-1"></i>This permit can't be approved until every {{ areaWord }} has a decision.
            </p>
        }

        @if (deciding) {
            <div class="card border-primary mt-3">
                <div class="card-body">
                    <h3 class="h6 mt-0">{{ decidingLabel }}</h3>

                    <div class="mb-2">
                        <label class="form-label small mb-1">Internal note (never shown to the applicant)</label>
                        <textarea class="form-control" rows="2" [(ngModel)]="staffNote" [disabled]="saving"></textarea>
                    </div>

                    <div class="mb-2">
                        <label class="form-label small mb-1">Note for the applicant (optional)</label>
                        <textarea class="form-control" rows="2" [(ngModel)]="applicantNote" [disabled]="saving"></textarea>
                    </div>

                    <div class="form-check mb-3">
                        <input class="form-check-input" type="checkbox" id="clanAreaVisible" [(ngModel)]="visibleToApplicant" [disabled]="saving" />
                        <label class="form-check-label small" for="clanAreaVisible">
                            Show this result to the applicant <span class="text-muted">(if unticked they'll keep seeing "Pending")</span>
                        </label>
                    </div>

                    <button type="button" class="btn btn-primary btn-sm me-1" (click)="save()" [disabled]="saving">Save decision</button>
                    <button type="button" class="btn btn-link btn-sm" (click)="cancel()" [disabled]="saving">Cancel</button>
                </div>
            </div>
        }

        @if (error) {
            <div class="alert alert-danger mt-2" role="alert">{{ error }}</div>
        }
    } @else {
        <p class="text-muted mb-0">-</p>
    }
}
